import React from "react";
import "@/App.css";
import "@/index.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider } from "./context/AuthContext";
import { ProtectedRoute, PublicRoute } from "./components/ProtectedRoute";
import { Toaster } from "./components/ui/sonner";
import ErrorBoundary from "./components/ErrorBoundary";

// Pages
import Login from "./pages/Login";
import AuthCallback from "./pages/AuthCallback";
import ResetPassword from "./pages/ResetPassword";
import VerifyEmail from "./pages/VerifyEmail";
import Welcome from "./pages/Welcome";
import Dashboard from "./pages/Dashboard";
import MainDashboard from "./pages/MainDashboard";
import EquipmentPage from "./pages/EquipmentPage";
import SubscriptionPage from "./pages/SubscriptionPage";
import BillingPage from "./pages/BillingPage";
import MaintenancePage from "./pages/MaintenancePage";
import ProfilePage from "./pages/ProfilePage";
import Invite from "./pages/Invite";
import ReferralLanding from "./pages/ReferralLanding";
import HelpCenter from "./pages/HelpCenter";
import RootRedirect from "./components/RootRedirect";

// Admin Pages
import {
  AdminDashboard,
  AdminClients,
  AdminReferrals,
  AdminRewards,
  AdminAnalytics,
  AdminLeads,
  AdminInstallations,
  AdminTickets,
  AdminUsers
} from "./pages/admin";
import AdminConfiguracion from "./pages/admin/AdminConfiguracion";

// Comercial Pages
import ComercialDashboard from "./pages/comercial/ComercialDashboard";
import NuevaPrueba from "./pages/comercial/NuevaPrueba";
import CRM from "./pages/comercial/CRM";
import ContactosPage from "./pages/comercial/Contactos";
import PruebasPage from "./pages/comercial/Pruebas";
import AgendaPage from "./pages/comercial/Agenda";
import ObjetivosPage from "./pages/comercial/Objetivos";
import PresupuestosPage from "./pages/comercial/Presupuestos";
import DocumentacionPage from "./pages/comercial/Documentacion";
import ReferidosComercialPage from "./pages/comercial/Referidos";
import InstalacionesPage from "./pages/comercial/Instalaciones";
import MisResultadosPage from "./pages/comercial/MisResultados";

// Técnico Pages
import TecnicoHome from "./pages/tecnico/TecnicoHome";
import AgendaTecnico from "./pages/tecnico/AgendaTecnico";
import VacacionesPage from "./pages/comercial/Vacaciones";
import FichajePage from "./pages/comercial/Fichaje";
import NuevoContacto from "./pages/comercial/NuevoContacto";
import FichaCliente from "./pages/comercial/FichaCliente";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
// Chequeo síncrono del fragmento OAuth de Google ANTES de renderizar las rutas normales.
function AppRoutes() {
  const location = useLocation();
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      {/* Raíz - la landing (pages/LandingPage.js) está aparcada: va al login */}
      <Route path="/" element={<RootRedirect />} />

      {/* Referral Landing - Public */}
      <Route path="/ref/:code" element={<ReferralLanding />} />

      {/* Public Routes */}
      <Route
        path="/login"
        element={
          <PublicRoute>
            <Login />
          </PublicRoute>
        }
      />
      {/* Registro público deshabilitado: las cuentas las crea Manklar (Portal Administración) */}
      <Route path="/register" element={<Navigate to="/login" replace />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/verificar-email" element={<VerifyEmail />} />

      {/* Protected Routes */}
      <Route
        path="/welcome"
        element={
          <ProtectedRoute>
            <Welcome />
          </ProtectedRoute>
        }
      />
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            <MainDashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/equipment"
        element={
          <ProtectedRoute>
            <EquipmentPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/subscription"
        element={
          <ProtectedRoute>
            <SubscriptionPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/billing"
        element={
          <ProtectedRoute>
            <BillingPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/maintenance"
        element={
          <ProtectedRoute>
            <MaintenancePage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/profile"
        element={
          <ProtectedRoute>
            <ProfilePage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/referrals"
        element={
          <ProtectedRoute>
            <Dashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/invite"
        element={
          <ProtectedRoute>
            <Invite />
          </ProtectedRoute>
        }
      />
      <Route
        path="/help"
        element={
          <ProtectedRoute>
            <HelpCenter />
          </ProtectedRoute>
        }
      />

      {/* Admin Routes - New Modular Structure */}
      <Route
        path="/admin"
        element={
          <ProtectedRoute adminOnly>
            <AdminDashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/clients"
        element={
          <ProtectedRoute adminOnly>
            <AdminClients />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/referrals"
        element={
          <ProtectedRoute adminOnly>
            <AdminReferrals />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/rewards"
        element={
          <ProtectedRoute adminOnly>
            <AdminRewards />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/analytics"
        element={
          <ProtectedRoute adminOnly>
            <AdminAnalytics />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/usuarios"
        element={
          <ProtectedRoute adminOnly>
            <AdminUsers />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/leads"
        element={
          <ProtectedRoute adminOnly>
            <AdminLeads />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/installations"
        element={
          <ProtectedRoute adminOnly>
            <AdminInstallations />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/tickets"
        element={
          <ProtectedRoute adminOnly>
            <AdminTickets />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/configuracion"
        element={
          <ProtectedRoute adminOnly>
            <AdminConfiguracion />
          </ProtectedRoute>
        }
      />

      {/* Portal Comercial Routes */}
      <Route
        path="/comercial"
        element={
          <ProtectedRoute allowedRoles={['admin', 'comercial']}>
            <ComercialDashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/comercial/nuevo-contacto"
        element={
          <ProtectedRoute allowedRoles={['admin', 'comercial']}>
            <NuevoContacto />
          </ProtectedRoute>
        }
      />
      <Route
        path="/comercial/nueva-prueba"
        element={
          <ProtectedRoute allowedRoles={['admin', 'comercial']}>
            <NuevaPrueba />
          </ProtectedRoute>
        }
      />
      <Route
        path="/comercial/crm"
        element={
          <ProtectedRoute allowedRoles={['admin', 'comercial']}>
            <CRM />
          </ProtectedRoute>
        }
      />
      {/* Ficha Cliente 360º */}
      <Route
        path="/comercial/cliente/:trialId"
        element={
          <ProtectedRoute allowedRoles={['admin', 'comercial']}>
            <FichaCliente />
          </ProtectedRoute>
        }
      />
      {/* Legacy route redirect */}
      <Route path="/comercial/pipeline" element={<Navigate to="/comercial/crm" replace />} />
      <Route
        path="/comercial/contactos"
        element={
          <ProtectedRoute allowedRoles={['admin', 'comercial']}>
            <ContactosPage />
          </ProtectedRoute>
        }
      />
      {/* Legacy route redirect */}
      <Route path="/comercial/leads" element={<Navigate to="/comercial/contactos" replace />} />
      <Route
        path="/comercial/pruebas"
        element={
          <ProtectedRoute allowedRoles={['admin', 'comercial']}>
            <PruebasPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/comercial/agenda"
        element={
          <ProtectedRoute allowedRoles={['admin', 'comercial']}>
            <AgendaPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/comercial/objetivos"
        element={
          <ProtectedRoute allowedRoles={['admin', 'comercial']}>
            <ObjetivosPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/comercial/presupuestos"
        element={
          <ProtectedRoute allowedRoles={['admin', 'comercial']}>
            <PresupuestosPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/comercial/documentacion"
        element={
          <ProtectedRoute allowedRoles={['admin', 'comercial', 'tecnico']}>
            <DocumentacionPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/comercial/referidos"
        element={
          <ProtectedRoute allowedRoles={['admin', 'comercial']}>
            <ReferidosComercialPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/comercial/instalaciones"
        element={
          <ProtectedRoute allowedRoles={['admin', 'comercial']}>
            <InstalacionesPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/comercial/mis-resultados"
        element={
          <ProtectedRoute allowedRoles={['admin', 'comercial']}>
            <MisResultadosPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/comercial/vacaciones"
        element={
          <ProtectedRoute allowedRoles={['admin', 'comercial', 'tecnico']}>
            <VacacionesPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/comercial/fichaje"
        element={
          <ProtectedRoute allowedRoles={['admin', 'comercial', 'tecnico']}>
            <FichajePage />
          </ProtectedRoute>
        }
      />

      {/* Portal Técnico */}
      <Route
        path="/tecnico"
        element={
          <ProtectedRoute allowedRoles={['admin', 'tecnico']}>
            <TecnicoHome />
          </ProtectedRoute>
        }
      />
      <Route
        path="/tecnico/agenda"
        element={
          <ProtectedRoute allowedRoles={['admin', 'tecnico']}>
            <AgendaTecnico />
          </ProtectedRoute>
        }
      />

      {/* Redirects */}
      <Route path="*" element={<Navigate to="/login" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <>
      <ErrorBoundary>
        <AuthProvider>
          <BrowserRouter>
            <AppRoutes />
          </BrowserRouter>
        </AuthProvider>
      </ErrorBoundary>
      <Toaster />
    </>
  );
}

export default App;
